import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { useOptimizedFamily } from "@/hooks/useOptimizedFamily";

interface FamilyModeContextType {
  isFamilyMode: boolean;
  setFamilyMode: (enabled: boolean) => void;
  hasFamily: boolean;
}

const FamilyModeContext = createContext<FamilyModeContextType | undefined>(undefined);

/**
 * Family Mode Provider.
 */
export function FamilyModeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [isFamilyMode, setIsFamilyMode] = useState(false);
  const { family } = useOptimizedFamily();

  // Auto-disable family mode if no family exists
  useEffect(() => {
    if (!family && isFamilyMode) {
      setIsFamilyMode(false);
    }
  }, [family, isFamilyMode]);

  const setFamilyMode = useCallback(
    (enabled: boolean) => {
      if (enabled && !family) {
        return; // Can't enable family mode without a family
      }
      setIsFamilyMode(enabled);
    },
    [family]
  );

  const value = useMemo(
    () => ({
      isFamilyMode,
      setFamilyMode,
      hasFamily: !!family,
    }),
    [isFamilyMode, setFamilyMode, family]
  );

  return <FamilyModeContext.Provider value={value}>{children}</FamilyModeContext.Provider>;
}

export function useFamilyMode() {
  const context = useContext(FamilyModeContext);
  if (context === undefined) {
    throw new Error("useFamilyMode must be used within a FamilyModeProvider");
  }
  return context;
}
